import React, { useContext, useState } from 'react'
import {
  BG_STYLES,
  BORDER_STYLES,
  COLORS,
  COMMON_STYLES,
  FilterIcon,
} from '@dehaat/dds'
import { Pressable, ScrollView, StyleSheet, View } from 'react-native'
import SearchInput from '../reusable/SearchInput'
import { ResponsiveContext } from 'app/context/ResponsiveUIContext'
import FilterModal from './FilterModal'
import RemovableFilterChip from 'app/components/reusable/RemovableFilterChip'

type VendorFilters = {
  dc_category?: string[]
  state?: { id: number; name: string }[]
  node?: { id: number; name: string }[]
}

type RequestHeaderProps = {
  searchQuery: string
  setSearchQuery: (val: string) => void
  setAppliedFilters: React.Dispatch<React.SetStateAction<VendorFilters>>
  appliedFilters: VendorFilters
}

const RequestHeader = ({
  searchQuery,
  setSearchQuery,
  setAppliedFilters,
  appliedFilters,
}: RequestHeaderProps) => {
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false)
  const { mediaStyles } = useContext(ResponsiveContext)

  const applyFiltersAndClose = (filters: VendorFilters) => {
    setAppliedFilters(filters)
    setIsFilterModalVisible(false)
  }

  const removeCategory = (category: string) => {
    setAppliedFilters(prev => ({
      ...prev,
      dc_category: prev.dc_category?.filter(item => item !== category),
    }))
  }

  const removeState = (stateId: number) => {
    setAppliedFilters(prev => ({
      ...prev,
      state: prev.state?.filter(item => item.id !== stateId),
    }))
  }

  const removeNode = (nodeId: number) => {
    setAppliedFilters(prev => ({
      ...prev,
      node: prev.node?.filter(item => item.id !== nodeId),
    }))
  }

  return (
    <View style={styles.header}>
      <View style={styles.container}>
        <SearchInput
          placeholder="Search by Name, Partner Id, Vendor Id"
          value={searchQuery}
          onChange={setSearchQuery}
          inputContainerStyle={[
            mediaStyles({ desktopStyle: styles.desktopSearch }),
          ]}
        />

        <ScrollView
          style={[
            COMMON_STYLES.row,
            BG_STYLES.bgWhite,
            mediaStyles({ desktopStyle: styles.desktopSearch }),
          ]}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ columnGap: 8, alignItems: 'center' }}
        >
          <Pressable
            style={styles.filterBtn}
            onPress={() => setIsFilterModalVisible(true)}
          >
            <FilterIcon
              svgProps={{
                height: 22,
                width: 22,
                fill: COLORS.neutral70,
              }}
            />
          </Pressable>

          {appliedFilters.dc_category?.map(category => (
            <RemovableFilterChip
              key={`dc_category_${category}`}
              label={category}
              onRemove={() => removeCategory(category)}
            />
          ))}

          {appliedFilters.state?.map(state => (
            <RemovableFilterChip
              key={`state_${state.id}`}
              label={state.name}
              onRemove={() => removeState(state.id)}
            />
          ))}

          {appliedFilters.node?.map(node => (
            <RemovableFilterChip
              key={`node_${node.id}`}
              label={node.name}
              onRemove={() => removeNode(node.id)}
            />
          ))}
        </ScrollView>
      </View>

      {isFilterModalVisible && (
        <FilterModal
          appliedFilterData={appliedFilters}
          onApplyFilters={applyFiltersAndClose}
          onClose={() => setIsFilterModalVisible(false)}
          filterPage="vendor"
        />
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  header: {
    ...BG_STYLES.bgWhite,
    ...COMMON_STYLES.fullWidth,
    borderBottomWidth: 1,
    ...BORDER_STYLES['borderNeutral20'],
    paddingHorizontal: 10,
  },
  container: {
    marginVertical: 16,
    gap: 10,
  },
  filterBtn: {
    width: 32,
    height: 32,
    padding: 4,
    ...BORDER_STYLES.borderNeutral20,
    ...COMMON_STYLES.b1,
    ...BG_STYLES.bgWhite,
    borderRadius: 5,
    marginRight: 12,
  },
  desktopSearch: {
    maxWidth: '70%',
    width: '100%',
    alignSelf: 'center',
  },
})

export default RequestHeader
